import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { broadcastMessage } from '../services/telegramService';

interface BroadcastStats {
  total: number;
  success: number;
  failed: number;
}

const BroadcastMessagePage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    userType: 'all',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [stats, setStats] = useState<BroadcastStats | null>(null);

  // Rediriger si l'utilisateur n'est pas admin
  React.useEffect(() => {
    if (user && !user.is_admin) {
      navigate('/');
    }
  }, [user, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Effacer les messages d'erreur/succès quand l'utilisateur tape
    if (error) setError('');
    if (success) setSuccess('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setStats(null);
    
    // Validation
    if (!formData.message.trim()) {
      setError('Le message ne peut pas être vide');
      return;
    }

    if (formData.message.length > 4000) {
      setError('Le message ne peut pas dépasser 4000 caractères');
      return;
    }
    
    setLoading(true);

    try {
      // Utiliser le service telegramService au lieu de fetch direct
      const response = await broadcastMessage(formData.userType, formData.message);
      
      setSuccess(response.message);
      setStats(response.stats);
      
      // Réinitialiser le formulaire après succès
      setFormData({
        userType: 'all',
        message: ''
      });
      
    } catch (err: any) {
      console.error('Erreur lors de la diffusion:', err);
      
      // Gestion d'erreur améliorée
      if (err.response) {
        setError(err.response.data?.detail || 'Erreur lors de l\'envoi du message');
      } else if (err.request) {
        setError('Impossible de contacter le serveur. Vérifiez votre connexion.');
      } else {
        setError(err.message || 'Une erreur inattendue s\'est produite');
      }
    } finally {
      setLoading(false);
    }
  };

  // Preview du message pour montrer comment il apparaîtra
  const renderMessagePreview = () => {
    if (!formData.message.trim()) return null;
    
    return (
      <div className="mt-4 p-4 bg-gray-50 border rounded-lg">
        <h4 className="text-sm font-medium text-gray-700 mb-2">Aperçu du message :</h4>
        <div 
          className="text-sm bg-white p-3 rounded border"
          dangerouslySetInnerHTML={{ __html: formData.message }}
        />
      </div>
    );
  };

  const getUserTypeLabel = (type: string) => {
    switch (type) {
      case 'all': return 'tous les utilisateurs';
      case 'farmer': return 'les agriculteurs';
      case 'customer': return 'les clients';
      case 'admin': return 'les administrateurs';
      default: return 'les destinataires sélectionnés';
    }
  };

  if (!user || !user.is_admin) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <div className="text-red-600 text-xl mb-4">Accès non autorisé</div>
          <p>Seuls les administrateurs peuvent accéder à cette page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-3xl mx-auto">
        {/* Header avec retour */}
        <div className="flex items-center mb-6">
          <button
            onClick={() => navigate('/admin')}
            className="mr-4 text-olive hover:text-terracotta transition"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-3xl font-bold text-slate">Diffusion de messages Telegram</h1>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          {/* Alertes */}
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {error}
              </div>
            </div>
          )}
          
          {success && (
            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-6">
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                {success}
              </div>
            </div>
          )}
          
          {/* Statistiques d'envoi */}
          {stats && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-blue-800 mb-3">📊 Statistiques d'envoi</h3>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">{stats.total}</div>
                  <div className="text-sm text-blue-700">Destinataires</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">{stats.success}</div>
                  <div className="text-sm text-green-700">Succès</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-600">{stats.failed}</div>
                  <div className="text-sm text-red-700">Échecs</div>
                </div>
              </div>
              {stats.total > 0 && (
                <div className="mt-3 text-sm text-blue-700">
                  Taux de réussite : {Math.round((stats.success / stats.total) * 100)}%
                </div>
              )}
            </div>
          )}
          
          {/* Formulaire */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-slate font-medium mb-2" htmlFor="userType">
                Destinataires
              </label>
              <select
                id="userType"
                name="userType"
                value={formData.userType}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-terracotta focus:border-transparent"
              >
                <option value="all">🌍 Tous les utilisateurs</option>
                <option value="farmer">🌾 Agriculteurs uniquement</option>
                <option value="customer">🛒 Clients uniquement</option>
                <option value="admin">⚙️ Administrateurs uniquement</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Le message sera envoyé à {getUserTypeLabel(formData.userType)} ayant lié leur compte Telegram.
              </p>
            </div>
            
            <div>
              <label className="block text-slate font-medium mb-2" htmlFor="message">
                Message
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows={8}
                placeholder="Entrez votre message ici...

Exemples de formatage HTML :
• <b>Texte en gras</b>
• <i>Texte en italique</i>
• <a href='https://example.com'>Lien</a>
• Utilisez des émojis pour rendre le message plus attrayant 🎉"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-terracotta focus:border-transparent"
                maxLength={4000}
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>Balises HTML autorisées : &lt;b&gt;, &lt;i&gt;, &lt;u&gt;, &lt;a href="..."&gt;</span>
                <span>{formData.message.length}/4000 caractères</span>
              </div>
            </div>

            {/* Aperçu du message */}
            {renderMessagePreview()}
            
            {/* Confirmation avant envoi */}
            {formData.message.trim() && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-yellow-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  <div className="text-yellow-800">
                    <strong>Attention :</strong> Ce message sera envoyé à {getUserTypeLabel(formData.userType)} immédiatement après confirmation.
                  </div>
                </div>
              </div>
            )}
            
            <button
              type="submit"
              disabled={loading || !formData.message.trim()}
              className="w-full bg-olive text-white py-3 px-4 rounded-md hover:bg-terracotta transition duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Envoi en cours...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                  Envoyer le message
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BroadcastMessagePage;